import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Loader } from "@/components/brand/Mascot";
import { useCart } from "@/context/CartContext";
import { getBundle } from "@/lib/api";
import { cad } from "@/lib/site";
import NotFound from "@/pages/NotFound";

const chip = (on) => `label h-10 min-w-[44px] rounded-full border-[1.5px] border-ink px-4 font-bold disabled:opacity-30 disabled:line-through ${on ? "bg-ink text-cloud" : "hover:bg-ink/5"}`;

const Slot = ({ slot, i, pick, setPick }) => {
  const p = slot.options.find((o) => o.slug === pick.slug);
  const choose = (o) => setPick({ slug: o.slug, color: o.colors[0].name, size: "OS" in o.sizes ? "OS" : "" });
  return (
    <li className="rounded-[24px] border-[1.5px] border-ink p-5" data-testid={`bundle-slot-${slot.key}`}>
      <p className="label flex items-center gap-2 font-bold"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink text-cloud">{pick.size ? <Check size={12} /> : i + 1}</span>{slot.label}</p>
      {slot.options.length > 1 && <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-5">{slot.options.map((o) => (
        <button key={o.slug} onClick={() => choose(o)} aria-pressed={pick.slug === o.slug} className={`tile aspect-square border-[1.5px] ${pick.slug === o.slug ? "border-ink" : "border-transparent"}`} title={o.name} data-testid={`bundle-option-${slot.key}-${o.slug}`}><img src={o.images[0].src} alt={o.name} className="h-full w-full object-cover mix-blend-multiply" /></button>))}</div>}
      {p && <>
        <p className="font-a mt-4">{p.name} <span className="text-ink/50">· {cad(p.price)} value</span></p>
        {p.colors.length > 1 && <div className="mt-3 flex flex-wrap gap-2">{p.colors.map((c) => <button key={c.name} onClick={() => setPick({ ...pick, color: c.name })} className={chip(pick.color === c.name)} data-testid={`bundle-color-${slot.key}-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>{c.name}</button>)}</div>}
        {!("OS" in p.sizes) && <div className="mt-3 flex flex-wrap gap-2">{Object.entries(p.sizes).map(([s, n]) => <button key={s} disabled={!n} onClick={() => setPick({ ...pick, size: s })} className={chip(pick.size === s)} data-testid={`bundle-size-${slot.key}-${s}`}>{s}</button>)}</div>}
      </>}
    </li>
  );
};

export default function Bundle() {
  const { slug } = useParams();
  const { addBundle } = useCart();
  const [b, setB] = useState(undefined);
  const [picks, setPicks] = useState({});
  useEffect(() => {
    getBundle(slug).then((d) => {
      setB(d);
      setPicks(Object.fromEntries(d.slots.map((s) => { const o = s.options.length === 1 ? s.options[0] : null; return [s.key, o ? { slug: o.slug, color: o.colors[0].name, size: "OS" in o.sizes ? "OS" : "" } : { slug: "", color: "", size: "" }]; })));
    }).catch(() => setB(null));
  }, [slug]);
  if (b === undefined) return <Loader label="Loading the bundle" />;
  if (!b) return <NotFound />;
  const ready = b.slots.every((s) => picks[s.key]?.size);
  const value = b.slots.reduce((a, s) => a + (s.options.find((o) => o.slug === picks[s.key]?.slug)?.price || Math.max(...s.options.map((o) => o.price))), 0);
  const add = () => addBundle(b, b.slots.map((s) => { const o = s.options.find((x) => x.slug === picks[s.key].slug); return { slot: s.key, ...picks[s.key], name: o.name, image: o.images[0].src }; }));
  return (
    <div className="mx-auto max-w-[1440px] px-5 pb-28 pt-12 sm:px-10 lg:pt-16" data-testid="bundle-page">
      <Seo title={b.name} description={b.description} path={`/bundles/${b.slug}`} image={b.image} />
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6"><img src={b.image} alt={b.name} className="w-full rounded-[24px] border-[1.5px] border-ink" /></div>
        <div className="lg:col-span-6">
          <p className="label text-ink/55">Bundle · save more</p>
          <h1 className="display mt-3 text-[clamp(3rem,5vw,4.75rem)]" data-testid="bundle-name">{b.name}</h1>
          <div className="mt-4 flex flex-wrap items-baseline gap-4"><span className="text-2xl" data-testid="bundle-price">{cad(b.price)}</span><span className="text-ink/50 line-through" data-testid="bundle-value">{cad(value)} value</span>
            {value > b.price && <span className="label rounded-full bg-pink px-3 py-1 font-bold" data-testid="bundle-save">You save {cad(value - b.price)}</span>}</div>
          <p className="mt-5 max-w-lg text-[15px] text-ink/75">{b.description}</p>
          <ol className="mt-8 space-y-4">{b.slots.map((s, i) => <Slot key={s.key} slot={s} i={i} pick={picks[s.key]} setPick={(v) => setPicks({ ...picks, [s.key]: v })} />)}</ol>
          <button disabled={!ready} onClick={add} className="btn btn-lime mt-8 w-full" data-testid="bundle-add-to-cart">{ready ? `Add bundle · ${cad(b.price)}` : "Pick every item first"} <ArrowRight size={16} /></button>
        </div>
      </div>
    </div>
  );
}
